import { Component, Injector, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ChargingStationsService } from '@services/entity/charging-stations.service';
import { UsersService } from '@services/entity/users.service';
import { BlackboxService } from '@services/entity/blackbox.service';
import { plainToClass } from 'class-transformer';
import '@angular/localize/init';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastService } from '@services/toast/toast.service';
import { Address } from '@models/address';
import { Customer } from '@models/customer';
import { CustomersService } from '@services/entity/customers.service';
import { Operator } from '@models/operator';
import { OperatorsService } from '@services/entity/operators.service';
import { MasterUserOfCustomer } from '@dto/masterUserOfCustomer';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Blackbox } from '@models/blackbox';
import { EnergyMeter } from '@models/energyMeter';

import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AbstractCreateEntityComponent } from '@abstracts/entities/create-entity';
import { ChargingStationDto } from '@dto/chargingStationDto';
import { ChargingStation } from '../../../../models/charging-station';
import { BlackboxSetup } from '@dto/blackboxSetup';
import { ChargingPointSignalColorDto } from '@dto/ChargingPointSignalColorDto';
import { EnergyMeterTypService } from '../../../../services/entity/energyMeterTyp.service';
import { EnergyMeterType } from '../../../../models/energyMeterType';

@Component({
  selector: 'blackbox-setup',
  templateUrl: './blackbox-setup.component.html',
  styleUrls: ['./blackbox-setup.component.scss']
})
export class BlackboxSetupComponent extends AbstractCreateEntityComponent implements OnInit {

  public className = 'BlackboxSetupComponent'

  public isVisible: boolean = false
  public isOperatorView: boolean
  public operator: Operator

  public isUser: boolean = false
  public isCustomer: boolean = false
  public isOperator: boolean = false

  @Input() serialNumber?: string = ''
  @Input() step: number = 1
  @Output() stepping = new EventEmitter();

  public entity: Blackbox
  public entities: Blackbox[] = []
  public entitiesAvailable: Blackbox[] = []

  public addresses: Address[] = []
  public customerAddress: Address
  public isAddressDone: boolean = false;

  public energyMeterTypes: EnergyMeterType[] = [];
  public energyMetersSelected: EnergyMeter[] = [];

  public customersAvailable: Customer[]
  public customerUserSelected: MasterUserOfCustomer
  public isCustomerDone: boolean = false;

  public chargingStationsAvailable: ChargingStationDto[] = []
  public chargingStationsAvailableForInputs: ChargingStationDto[] = []
  public chargingStationsAtAddress: ChargingStationDto[] = []

  public defaultOuterColors: string[]
  public defaultInnerColors: string[]
  public colorsEditable: ChargingPointSignalColorDto[] = []
  public editingColor: ChargingPointSignalColorDto
  public editedStatusColors: ChargingPointSignalColorDto[] = []
  public feColorMatrix: string

  constructor(
    public chargingStationsService: ChargingStationsService,
    public operatorsService: OperatorsService,
    public customersService: CustomersService,
    public usersService: UsersService,
    public toastService: ToastService,
    public sanitizer: DomSanitizer,
    public blackboxesService: BlackboxService,
    public energyMeterTypService: EnergyMeterTypService,
    public activeModal: NgbActiveModal,
    injector: Injector
  ) {
    super(blackboxesService, injector);
  }

  ngOnInit() {
    super.ngOnInit()
    this.getEntities()
    this.getCustomers()
  }

  getEntities() {
    this.blackboxesService.getAll().subscribe(result => {
      this.entities = plainToClass(Blackbox, result);
      this.entitiesAvailable = this.entities.filter(bb => bb.customer == null);

      if (this.serialNumber != null) {
        this.entity = this.entities.find(bb => bb.serialNumber == this.serialNumber);
        this.createForm()
        this.editingColor = this.entity.chargingPointSignalColors[1]
        this.defaultOuterColors = this.entity.chargingPointSignalColors.map(i => i.insideColor.hex)
        this.defaultInnerColors = this.entity.chargingPointSignalColors.map(i => i.outsideColor.hex)
        this.colorsEditable = this.entity.chargingPointSignalColors
          .filter(i =>
            i.chargingPointSignal.toString() == 'Idle' ||
            i.chargingPointSignal.toString() == 'Authentication' ||
            i.chargingPointSignal.toString() == 'Reading' ||
            i.chargingPointSignal.toString() == 'Error' ||
            i.chargingPointSignal.toString() == 'Reserved'
          )

        this.formService.form.get('customer').patchValue(this.entity.customer);
        this.formService.form.get('address').patchValue(this.entity.address);

        for (const energyMeter of this.entity.energyMeters) {
          if (energyMeter.isAlive) {
            this.addEnergyMeter(energyMeter);
          }
        }

        this.getMasterUserAndChargingStations();
      }
    })

    this.energyMeterTypService.getEnergyMeterTyps().subscribe(result => {
      this.energyMeterTypes = result;
    });
  }

  selectEntity() {
    this.entity = this.entities.find(bb => bb.serialNumber == this.formService.form.get('serialNumber').value.serialNumber);
  }

  getCustomers() {
    this.usersService.getOperatorOfUser(this.usersService.getCurrentUserId()).subscribe(result => {
      this.operator = plainToClass(Operator, result);
      this.operatorsService.getCustomersTableOfOperator(this.operator.id).subscribe(result => {
        this.customersAvailable = plainToClass(Customer, result);
        this.customersAvailable.sort((a, b) => b.customerId - a.customerId)
      })
    })
  }

  getChargingStations() {
    if (!this.formService.form.get('customer').value) return
    let customerId = this.formService.form.get('customer').value.id
    this.chargingStationsService.getChargingStationsOfCustomer(customerId).subscribe(result => {
      const chargingStations = plainToClass(ChargingStation, result)

      this.getUniqueAddressesFromChargingStations(chargingStations)

      this.chargingStationsAvailable = chargingStations.filter(cs => cs.name != null)
      this.chargingStationsAtAddress = chargingStations.filter(cs => cs.address && cs.address.street == this.customerUserSelected.street)

      this.setChargingStationsFormArray()
    })
  }

  getUserAddress(userId: string) {
    this.usersService.getUserAddressById(userId).subscribe(result => {
      this.customerAddress = result;
    })
  }

  setChargingStationsFormArray() {
    const formGroup = this.formService.form.get('chargingStations') as FormGroup

    for (let i = 0; i < this.chargingStationsAvailable.length; i++) {
      const chargingStation = this.chargingStationsAvailable[i]
      const address = chargingStation.address
      let isPreselected = false

      if (this.entity.customerId == null) {
        isPreselected = address && address.street == this.customerUserSelected.street || false
      } else {
        isPreselected = this.entity.chargingStations.find(cs => cs.id == chargingStation.id) != null
      }

      formGroup.addControl(
        chargingStation.id,
        new FormControl(isPreselected)
      )
    }

    this.chargingStationsAvailableForInputs = this.chargingStationsAvailable
    this.chargingStationsAvailableForInputs.sort((a, b) => a.name.localeCompare(b.name));
  }

  getUniqueAddressesFromChargingStations(chargingStations: ChargingStationDto[]) {
    let uniqueAddresses: string[] = []
    this.addresses = []

    for (const station of chargingStations) {
      const address = station.address
      if (address == null || address.street == null) continue
      const line = `${address.street} ${address.houseNumber} ${address.postalCode} ${address.city}`
      if (uniqueAddresses.includes(line)) continue
      uniqueAddresses.push(line)
      this.addresses.push(station.address)
    }

    if (this.addresses.length == 0) this.addresses.push(this.customerAddress)

    this.formService.form.patchValue({
      address: this.addresses[0]
    })
    this.isAddressDone = this.formService.form.value.address?.id != null;
  }

  addEnergyMeter(energyMeter?: EnergyMeter) {
    const formGroup: FormGroup = new FormGroup({
      id: new FormControl(energyMeter?.id),
      name: new FormControl(energyMeter?.name),
      energyMeterType: new FormControl(energyMeter?.energyMeterType),
      ipAddress: new FormControl(energyMeter?.ipAddress),
      macAddress: new FormControl(energyMeter?.macAddress),
      showInverted: new FormControl(energyMeter?.showInverted ?? false),
      recordInverted: new FormControl(energyMeter?.recordInverted ?? false),
      powerLimitInKw: new FormControl(energyMeter?.powerLimitInKw ?? 0)
    });

    const energyMeters = this.formService.form.get('energyMeters') as FormArray;
    energyMeters.push(formGroup);
  }


  removeEnergyMeter(energyMeterIndex) {
    let energyMeters = this.formService.form.get('energyMeters') as FormArray
    energyMeters.removeAt(energyMeterIndex)
  }

  addStatusColor(color: ChargingPointSignalColorDto) {
    if (this.editedStatusColors.find(i => i.chargingPointSignal == this.editingColor.chargingPointSignal))
      return;

    color.blackboxId = this.entity.id;
    this.editedStatusColors.push(color)
  }

  resetStatusColor() {
    this.editedStatusColors = this.editedStatusColors
      .filter(i => i.chargingPointSignal != this.editingColor.chargingPointSignal)

    const index: number = this.entity.chargingPointSignalColors.indexOf(this.editingColor);

    this.editingColor.insideColor.hex = this.defaultOuterColors[index]
    this.editingColor.outsideColor.hex = this.defaultInnerColors[index]
  }

  createForm() {
    this.formService.validators = {
      serialNumber: [Validators.required],
      customer: [Validators.required],
      address: [Validators.required],
      chargingStations: [Validators.required],
      energyMeters: [Validators.required],
      chargingPointSignalColors: [],
    }

    this.formService.form = new FormGroup({
      serialNumber: new FormControl(this.serialNumber, this.formService.validators['serialNumber']),
      customer: new FormControl(this.entity?.customer, this.formService.validators['customer']),
      address: new FormControl(this.entity?.address, this.formService.validators['address']),
      chargingStations: new FormGroup({}, this.formService.validators['chargingStations']),
      energyMeters: new FormArray([], this.formService.validators['energyMeters']),
    })
  }

  get chargingStations() {
    return (this.formService.form.get("chargingStations") as FormArray).controls
  }

  get energyMeters() {
    return (this.formService.form.get('energyMeters') as FormArray).controls
  }

  onSubmit() {
    const form = this.formService.form.value;

    console.log("form", form)

    if (!form.customer) this.toastService.warn(`Bitte Kunde ausgewählen.`);
    if (!form.address) this.toastService.warn(`Bitte Adresse ausgewählen.`);
    if (form.energyMeters.length < 1) this.toastService.warn(`Bitte Energiemeter anlegen.`);

    form.energyMeters.forEach((meter: EnergyMeter) => {
      meter.energyMeterTypeId = meter.energyMeterType?.id;
      meter.energyMeterType = null;
      if(!meter.powerLimitInKw)
        meter.powerLimitInKw = 0;
    });

    let setup = new BlackboxSetup({
      serialNumber: form.serialNumber.serialNumber ?? this.serialNumber,
      customerId: form.customer.id,
      addressId: form.address.id,
      chargingStations: form.chargingStations,
      energyMeters: form.energyMeters,
      chargingPointSignalColors: this.editedStatusColors,
    });


    this.blackboxesService.setup(setup).subscribe(result => {
      this.toastService.success(`Blackbox erfolgreich eingerichtet.`);
      this.activeModal.close();
    }, error => {
      this.toastService.error(`Fehler beim einrichten der Blackbox.`);
    })
  }

  editColor(status: ChargingPointSignalColorDto) {
    this.editingColor = status
  }

  getMasterUserAndChargingStations() {
    if (!this.formService.form.get('customer').value) return
    let customerId = this.formService.form.get('customer').value.id;
    this.isCustomerDone = true;
    this.customersService.getMasterUserOfCustomer(customerId).subscribe(result => {
      this.customerUserSelected = plainToClass(MasterUserOfCustomer, result);
      this.getChargingStations();
      this.getUserAddress(this.customerUserSelected.id);
    })
  }

  public previousKeyCode: number = 0
  tabToStep(event) {
    if (this.previousKeyCode == 16 && event.keyCode == 9 && this.step > 1) {
      this.stepPrevious()
    } else if (event.keyCode == 9 && this.step < 5) {
      this.stepNext()
    }
    this.previousKeyCode = event.keyCode
  }

  tabToPreviousStep(event) {
    if (this.previousKeyCode == 16 && event.keyCode == 9 && this.step > 1) {
      this.stepPrevious()
    }
    this.previousKeyCode = event.keyCode
  }

  tabToNextStep(event) {
    if (this.previousKeyCode != 16 && event.keyCode == 9 && this.step < 5) {
      this.stepNext()
    }
    this.previousKeyCode = event.keyCode
  }

  stepPrevious() { this.step -= 1; this.stepping.emit(this.step) }
  stepNext() { this.step += 1; this.stepping.emit(this.step) }

  setStatusColor(part: string, event: any) {
    const value = event.target.value
    if (part == 'inside') this.editingColor.insideColor.hex = value
    if (part == 'outside') this.editingColor.outsideColor.hex = value
    if (part == 'both') {
      this.editingColor.outsideColor.hex = value
      this.editingColor.insideColor.hex = value
    }
    this.setFeColorMatrix(value)
    this.addStatusColor(this.editingColor)
  }

  setFeColorMatrix(hex) {
    let rgb: number[] = [];
    let matrix: number[] = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

    for (let i = 0; i < hex.length; i += 2) {
      const firstDigit = parseInt(hex[i], 16);
      const firstDigitPartial = firstDigit * 16;

      let v = parseInt(hex[i + 1], 16) + firstDigitPartial;
      v = v / 255;
      rgb.push(parseInt(v.toFixed(2)));
    }

    const red = rgb[0];
    const green = rgb[1];
    const blue = rgb[2];

    matrix[0] = red * .1;
    matrix[6] = green * .1;
    matrix[12] = blue * .1;

    this.feColorMatrix = matrix.join(' ');
  };
}
